import { useSyncExternalStore } from 'react';
import { useSelector } from 'react-redux';

import { DEPLOYMENT_REGIONS, deploymentRegion } from 'app/constants';
import useLoggedInUser from 'app/hooks/useLoggedInUser';
import useOrganisationFeatures from 'app/hooks/useOrganisationFeatures';
import type { RootState } from 'app/store';

import newlyIncludedPageIds from '../newlyIncludedPageIds';
import {
  markPageSeen,
  parseSeenPages,
  readSeenPages,
  subscribeToSeenPages,
} from '../newlyIncludedPagesSeen';

export interface NewlyIncludedPages {
  /** The new pricing added the page to the plan and the user hasn't opened it since. */
  isNewlyIncluded: (itemId: string) => boolean;
  markSeen: (itemId: string) => void;
}

/**
 * The pages that carry a green dot once the new pricing launches, until the user opens them. Only
 * UK customers moved onto the new plans with pages added.
 */
export default function useNewlyIncludedPages(): NewlyIncludedPages {
  const userId = useLoggedInUser()?._id;
  const { plan, pricingLaunched } = useOrganisationFeatures();
  const isPro = useSelector(
    (state: RootState) => state.pharmacySelector.selectedPharmacy?.chain?.pro?.enabled === true
  );
  const stored = useSyncExternalStore(subscribeToSeenPages, () =>
    userId ? readSeenPages(userId) : null
  );

  const pageIds =
    userId && pricingLaunched && deploymentRegion === DEPLOYMENT_REGIONS.UK
      ? newlyIncludedPageIds(plan, isPro)
      : [];
  const seen = parseSeenPages(stored);

  return {
    isNewlyIncluded: (itemId) => pageIds.includes(itemId) && !seen.includes(itemId),
    markSeen: (itemId) => {
      if (userId && pageIds.includes(itemId)) markPageSeen(userId, itemId);
    },
  };
}
